/** Internal id of a row in the shared `tenants` table. */
export type TenantId = string;

/**
 * Colour scheme for the Experience page, per tenant.
 *
 * The host does not forward its own theme — it decides dark from a realm flag
 * and a `data-document-theme` attribute on its `<html>`, and a cross-origin
 * iframe can read neither — so the tenant row is what tells this extension
 * which one it is sitting inside.
 */
export const TENANT_THEMES = ['light', 'dark'] as const;

export type TenantTheme = (typeof TENANT_THEMES)[number];

export const DEFAULT_TENANT_THEME: TenantTheme = 'light';

export function isTenantTheme(value: unknown): value is TenantTheme {
  return typeof value === 'string' && (TENANT_THEMES as readonly string[]).includes(value);
}

/**
 * A Kadanza tenant linked to this deployment.
 *
 * Tenants are rows rather than infrastructure: one D1 database and one R2
 * bucket serve every tenant of a deployment, and isolation comes from
 * `tenant_id` on every other table plus tenant-prefixed R2 keys.
 */
export interface Tenant {
  id: TenantId;
  /** UUID from the extension JWT's `tenant_id` claim. The only authorization input. */
  kadanzaTenantId: string;
  /** Sent as the `x-tenant` header on Kadanza API calls, e.g. `shopx-staging`. */
  tenantDomain: string;
  /** Origin of the Kadanza API for this tenant, e.g. `https://shopx.studiox.coke.com`. */
  kadanzaApiBaseUrl: string;
  label: string;
  theme: TenantTheme;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Everything downstream of authentication carries a resolved tenant, never a raw claim. */
export interface Principal {
  tenant: Tenant;
  userId: string;
  scopes: string[];
  extensionInstallId?: string;
}
